/* @flow */

import * as React from 'react';
import { withHandlers } from 'recompose';
import glamorous from 'glamorous';
import { borderRadius } from 'polished';

import Tappable from 'app/components/commons/Tappable';
import PollingCircleFastForward from 'app/components/commons/PollingCircleFastForward';
import { hover } from 'app/utils/cssUtils';
import t from 'app/utils/translate';
import { LEADERBOARD, PLAYERBOARD } from 'app/constants/liveChallengeTabTypes';
import RowHeader from 'app/components/commons/RowHeader';
import OddsProvidedBy from 'app/components/challenge/OddsProvidedBy';

type Props = {
  isInProgress?: boolean,
  isRacing?: boolean,
  onLeaderboardClick: () => void,
  onPlayerboardClick: () => void,
  pollTimestamp?: number,
  selected?: string
};

const defaultProps = {
  isInProgress: false,
  isRacing: false,
  selected: LEADERBOARD,
  pollTimestamp: undefined
};

const PollingCircleContainer = glamorous.div(({ theme }) => ({
  color: theme.color.primary,
  marginRight: theme.spacing.small
}));

const Tab = glamorous.div(({ selected, theme }) => ({
  alignItems: 'center',
  color: selected ? theme.color.white : theme.color.basis5,
  display: 'flex',
  fontSize: theme.fontSize.xsmall,
  margin: `0 ${theme.spacing.small}px`,
  textTransform: 'uppercase',
  transition: 'color 0.2s',
  ...hover({
    color: theme.color.white
  })
}));

const TabContainer = glamorous.div({
  display: 'flex'
});

const RightWrapper = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const StyledOddsProvidedBy = glamorous(OddsProvidedBy)(({ theme }) => ({
  ...borderRadius('top', theme.borderRadius.default)
}));

function LiveChallengeTabs({
  isInProgress,
  isRacing,
  onLeaderboardClick,
  onPlayerboardClick,
  pollTimestamp,
  selected
}: Props) {
  const showOddsProvidedBy = false;

  return (
    <RowHeader first isTableHeader stickyOffset={-1} noPadding={showOddsProvidedBy && isRacing}>
      <TabContainer>
        <Tappable component={Tab} onTap={onLeaderboardClick} selected={selected === LEADERBOARD}>
          {t('leaderboard')}
        </Tappable>
        <Tappable component={Tab} onTap={onPlayerboardClick} selected={selected === PLAYERBOARD}>
          {t(isRacing ? 'raceboard' : 'playerboard')}
        </Tappable>
      </TabContainer>
      <RightWrapper>
        {isInProgress && (
          <PollingCircleContainer>
            <PollingCircleFastForward pollTimestamp={pollTimestamp} />
          </PollingCircleContainer>
        )}
        {showOddsProvidedBy && isRacing && <StyledOddsProvidedBy />}
      </RightWrapper>
    </RowHeader>
  );
}

LiveChallengeTabs.defaultProps = defaultProps;

const enhancer = withHandlers({
  onLeaderboardClick: ({ onClick }) => () => {
    onClick(LEADERBOARD);
  },
  onPlayerboardClick: ({ onClick }) => () => {
    onClick(PLAYERBOARD);
  }
});

export default enhancer(LiveChallengeTabs);
